import {
  ROADMAP_STEP_MS,
  SOURCE_GLYPHS,
  analysisAt,
  channelAt,
  chatAt,
  previewChannel,
  type AnalysisFrame,
  type ChatFrame,
  type PreviewChannel,
} from '../roadmap';
import { $, $$, segmented, text } from './dom';
import { track } from './events';
import { inView, reducedMotion, whileVisible } from './motion';

/* -------------------------------------------------------------------------
 * "What we’re building next": three looping previews on one 1 s ticker,
 * which starts when the section is first on screen and skips its steps while
 * the tab is hidden or the section is scrolled away. Under reduced motion
 * nothing loops: the server's last frames stay, and the channel buttons
 * still switch their preview.
 * ---------------------------------------------------------------------- */

/** 01 · The analysis panel at one step. */
function paintAnalysis(root: HTMLElement, f: AnalysisFrame): void {
  const status = $(root, '[data-rm-status]');
  status?.classList.toggle('is-ready', f.phase === 'ready');
  text($(root, '[data-rm-status-text]'), f.label);
  text($(root, '[data-rm-read]'), String(f.read));

  $$(root, '[data-rm-src]').forEach((chip, i) => {
    const state = f.sources[i];
    chip.classList.remove('is-waiting', 'is-reading', 'is-read');
    chip.classList.add(`is-${state}`);
    text($(chip, '[data-rm-glyph]'), SOURCE_GLYPHS[state]);
  });

  // The bar eases forward a step at a time. Empty is the only way back, and
  // it jumps there rather than sliding backwards.
  const bar = $(root, '[data-rm-bar]');
  if (bar) {
    const jump = f.progress === 0;
    bar.classList.toggle('is-jump', jump);
    bar.style.width = `${f.progress * 100}%`;
    if (jump) {
      void bar.offsetWidth;
      bar.classList.remove('is-jump');
    }
  }

  $$(root, '[data-rm-find]').forEach((row, i) =>
    row.classList.toggle('is-pending', i >= f.findings),
  );
}

/**
 * 02 · The chat at one step. Showing a pair that was hidden replays its
 * bubbles' entrance, as does unhiding the typing dots or the answer.
 */
function paintChat(root: HTMLElement, f: ChatFrame): void {
  $$(root, '[data-rm-pair]').forEach((pair, i) => {
    const on = i === f.pair;
    // A new pair always comes in from the top: its question alone.
    if (on && pair.hidden) {
      for (const el of $$(pair, '[data-rm-typing], [data-rm-answer]'))
        el.hidden = true;
    }
    pair.hidden = !on;
    if (!on) return;
    const typing = $(pair, '[data-rm-typing]');
    const answer = $(pair, '[data-rm-answer]');
    if (typing) typing.hidden = !f.typing;
    if (answer) answer.hidden = !f.answered;
  });
}

/** 04 · One channel's preview; turning it on replays its entrance. */
function showChannel(root: HTMLElement, channel: PreviewChannel): void {
  for (const pane of $$(root, '[data-rm-pane]')) {
    pane.classList.toggle('is-on', pane.dataset.rmPane === channel);
  }
}

/** The section's previews; returns what stops every one of their timers. */
export function roadmap(root: HTMLElement): () => void {
  const analysis = $(root, '[data-rm-analysis]');
  const chat = $(root, '[data-rm-chat]');
  const channels = $(root, '[data-rm-channels]');
  const group = channels && $(channels, '[role="group"]');

  let pinned = false;
  const setChannel = group
    ? segmented(group, (value) => {
        const channel = previewChannel(value);
        if (!channel || !channels) return;
        pinned = true;
        showChannel(channels, channel);
        track('landing_roadmap_channel_clicked', { channel });
      })
    : null;

  const paint = (step: number) => {
    if (analysis) paintAnalysis(analysis, analysisAt(step));
    if (chat) paintChat(chat, chatAt(step));
    if (channels && setChannel && !pinned) {
      const channel = channelAt(step);
      setChannel(channel);
      showChannel(channels, channel);
    }
  };

  // "In view" is the hero card's 20%, not the section's: on a short phone
  // screen the whole section is too tall ever to be 20% on screen.
  const hero = $(root, '.rm-hero') ?? root;
  const motion = !reducedMotion();
  let step = 0;
  // Rewind now, while the section is most likely further down the page, so
  // it is at step 0 when it scrolls in. Without motion the server's last
  // frames stay.
  if (motion) paint(step);

  // Off screen, the loops hold their step rather than run unseen.
  let onScreen = !('IntersectionObserver' in window);
  const seen = onScreen
    ? null
    : new IntersectionObserver((entries) => {
        onScreen = entries.some((e) => e.isIntersecting);
      });
  seen?.observe(root);

  let ticker: number | undefined;
  const unwatch = inView(hero, () => {
    track('landing_roadmap_viewed', {});
    if (!motion) return;
    onScreen = true;
    ticker = whileVisible(() => {
      if (!onScreen) return;
      step += 1;
      paint(step);
    }, ROADMAP_STEP_MS);
  });

  return () => {
    window.clearInterval(ticker);
    seen?.disconnect();
    unwatch();
  };
}
